import { useCallback, useEffect, useMemo, useState } from "react";
import TopTemplate from "../../common/templates/top/TopTemplate";
import { PageTitle } from "../../common/PageTitle";
import { Box, BoxBackGround } from "../../common/Box";
import {
  ButtonPriority,
  ButtonType,
  ButtonWidth,
  ButtonWrapper,
  TextSize,
} from "../../common/Button";
import { Heading, HeadingType } from "../../common/Heading";
import {
  UnOrderedList,
  UnOrderedListItem,
} from "../../common/List/UnOrderedList/UnOrderedList";
import { Text, TextType } from "../../common/Text";
import { ModLink } from "../../common/Link";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
  TableStyle,
} from "../../common/Table";
import axios from "axios";
import { config } from "../../common/config/Config";

import style from "./AuthenticationList.module.css";
import { DateFormater as format } from "../../common/DateFormat/DateFormat";
import { ModalModule } from "../../common/Modal";
import { TextBox } from "../../common/Form/TextBox";
import { LoadingIcon } from "../../common/LoadingIcon/LoadingIcon";
import { getUrlConfig } from "../../common/urlCreator/urlConfig";
import { validateTermName } from "../../common/TerminalNameValidator/termValidator";
import {
  BIOMETRIC_INFO_PAGE_URL,
  EMPTY_TERMINAL_NAME_ERROR,
  GET_LIST_AUTHENTICATOR_ERROR,
  SYSTEM_ERROR,
  TERMINAL_NAME_VALIDATOR_FAILED_ERROR,
} from "../../common/Constant/Constant";
import { UserInfoView } from "../UserInfoView";

interface Authenticator {
  id: string;
  deviceName: string;
  registerDate: string;
}

interface UserInfo {
  usr_id: string;
  nickname: string;
}

enum ModalType {
  DEFAULT = 0,
  DELETE = 1,
  EDIT = 2,
}

const initAuthenticator: Authenticator = {
  id: "",
  deviceName: "登録なし	",
  registerDate: "日時がありません",
};

const initalAuthenticators = () => {
  return new Array(config.maxNumberRegisterDevice).fill(initAuthenticator);
};

const urlConfig = getUrlConfig();

const CSAuthenticatorManager = () => {
  const [authenticators, setAuthenticators] =
    useState<Authenticator[]>(initalAuthenticators);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(ModalType.DEFAULT);
  const [selectedAuthenticator, setSelectedAuthenticator] =
    useState<Authenticator>(initAuthenticator);
  const [authenticatorCount, setAuthenticatorCount] = useState(0);
  const [commonErrorMessage, setCommonErrorMessage] = useState<string>();
  const [showError, setShowError] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState("");
  const [modalShowError, setModalShowError] = useState(false);
  const [usrInfo, setUsrInfo] = useState<UserInfo>({
    usr_id: "idpuserdummy@test",
    nickname: "Test User",
  });

  const prsnl_id = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    let prsnl_id_param = params.get("id");
    return prsnl_id_param ?? "";
  }, [window.location.search]);

  useEffect(() => {
    retrieveUserInformation();
  }, []);

  useEffect(() => {
    if (!openModal && isProcessing) {
      // Re-active OK button after close update/delete modal
      setIsProcessing(false);
    }
  }, [openModal]);

  const retrieveUserInformation = useCallback(async () => {
    if (!prsnl_id) {
      setCommonErrorMessage(GET_LIST_AUTHENTICATOR_ERROR);
      setShowError(true);
      return;
    }
    try {
      //TODO: Get user information
      //Get list register authenticator
      retrieveAuthenticators(prsnl_id);
    } catch (err) {
      setCommonErrorMessage(GET_LIST_AUTHENTICATOR_ERROR);
      setShowError(true);
    }
  }, [prsnl_id]);

  const retrieveAuthenticators = useCallback(async (prsnl_id: string) => {
    try {
      setLoadingState(true);
      const res = await axios.post(
        `${urlConfig.SONET_AUTH_DEVICE_API_CS}${config.apiBaseUrl}${config.apiCSAuthenticatorGet}`,
        {
          prsnl_id: prsnl_id,
        },
        {
          withCredentials: true
        }
      );
      console.log(res);

      const data = res.data;

      const authenticators = data.authenticators
        .filter((item: any) => !item.is_deleted)
        .map(
          (item: any): Authenticator => ({
            id: item.credential_id,
            deviceName: item.authenticator_name,
            registerDate:
              item.created_at &&
              format(item.created_at),
          })
        ).sort((n1: any, n2: any) => {
          if (n1.registerDate < n2.registerDate) {return 1;}
          if (n1.registerDate > n2.registerDate) {return -1;}
          return 0;
        });

      const displayAuthenticators = [
        ...authenticators,
        ...initalAuthenticators(),
      ].slice(0, config.maxNumberRegisterDevice);

      setAuthenticatorCount(authenticators.length);
      setAuthenticators(displayAuthenticators);
    } catch (err) {
      setCommonErrorMessage(GET_LIST_AUTHENTICATOR_ERROR);
      setShowError(true);
    } finally {
      setLoadingState(false);
    }
  }, []);

  const onUpdateButtonClick = useCallback((authenticator: Authenticator) => {
    setSelectedAuthenticator(authenticator);
    setModalType(ModalType.EDIT);
    setOpenModal(true);
  }, []);

  const onDeleteButtonClick = useCallback((authenticator: Authenticator) => {
    setSelectedAuthenticator(authenticator);
    setModalType(ModalType.DELETE);
    setOpenModal(true);
  }, []);

  const onModalCloseButtonClick = useCallback(() => {
    setOpenModal(false);
    setModalShowError(false);
  }, []);

  const onUpdateModalSubmitButtonClick = useCallback(
    (newAuthenticatorName: string) => {
      setShowError(false);
      const newName = newAuthenticatorName.trim();
      if (!newName) {
        setModalErrorMessage(EMPTY_TERMINAL_NAME_ERROR);
        setModalShowError(true);
        return;
      }
      if (!validateTermName(newName)) {
        setModalErrorMessage(TERMINAL_NAME_VALIDATOR_FAILED_ERROR);
        setModalShowError(true);
        return;
      }
      setIsProcessing(true);
      const updateAuthenticator = async () => {
        try {
          const res = await axios.post(
            `${urlConfig.SONET_AUTH_DEVICE_API_CS}${config.apiBaseUrl}${config.apiCSAuthenticatorUpdate}`,
            {
              credential_id: selectedAuthenticator.id,
              old_name: selectedAuthenticator.deviceName,
              authenticator_name: newAuthenticatorName,
              prsnl_id: prsnl_id,
            },
            {
              withCredentials: true
            }
          );
        } catch (err) {
          showSystemError();
        } finally {
          setOpenModal(false);
          setModalShowError(false);
          retrieveAuthenticators(prsnl_id);
        }
      };
      updateAuthenticator();
    },
    [selectedAuthenticator, prsnl_id, usrInfo]
  );

  const onDeleteModalSubmitButtonClick = useCallback(() => {
    setShowError(false);
    setIsProcessing(true);
    const deleteAuthenticator = async () => {
      try {
        await axios.post(
          `${urlConfig.SONET_AUTH_DEVICE_API_CS}${config.apiBaseUrl}${config.apiCSAuthenticatorDelete}`,
          {
            credential_id: selectedAuthenticator.id,
            authenticator_name: selectedAuthenticator.deviceName,
            prsnl_id: prsnl_id,
          },
          {
            withCredentials: true
          }
        );
        retrieveAuthenticators(prsnl_id);
      } catch (err) {
        showSystemError();
      } finally {
        setOpenModal(false);
      }
    };
    deleteAuthenticator();
  }, [selectedAuthenticator, prsnl_id, usrInfo]);

  const showSystemError = () => {
    setCommonErrorMessage(SYSTEM_ERROR);
    setShowError(true);
  };

  return (
    <>
      <TopTemplate>
        <>
          <PageTitle
            text="生体認証ログイン設定"
            title="生体認証ログイン設定（CS用）"
          />
          <UserInfoView prsnl_id={prsnl_id}/>
          {showError && commonErrorMessage ? (
            <ErrorMessageForm errorMessages={commonErrorMessage} />
          ) : null}
          <Heading
            type={HeadingType.B}
            level={3}
            text="指紋・顔認証のログイン設定"
          />
          <Text
            textItems={[
              {
                text: "生体認証機能が搭載された端末を登録することで、パスワードを入力する代わりに、指紋・顔認証などを使ってログインできるようになります。",
              },
            ]}
            adjust={{ mb: 8 }}
          />
          <ModLink
            href={BIOMETRIC_INFO_PAGE_URL}
            activeForAgent={true}
            children="生体認証の詳細はこちら"
          />
          <ButtonWrapper
            textSize={TextSize.NORMAL}
            buttons={[
              {
                text: "この端末を登録する",
                type: ButtonType.BUTTON,
                width: ButtonWidth.WIDE,
                priority: ButtonPriority.HIGH,
                disabled: true,
              },
            ]}
          />
          <Heading
            type={HeadingType.B}
            level={3}
            text="登録済みの端末"
            id="anc-01"
          />
          <AuthenticatorTablePresentation
            isLoading={loadingState}
            authenticators={authenticators}
            onUpdateButtonClick={onUpdateButtonClick}
            onDeleteButtonClick={onDeleteButtonClick}
          />
          <Text
            textItems={[
              {
                type: TextType.SYMBOL,
                text: `${config.maxNumberRegisterDevice}台まで登録できます。`,
              },
            ]}
            adjust={{ mb: 8 }}
            className={style["text-hint"]}
          />
          <ButtonWrapper
            textSize={TextSize.NORMAL}
            buttons={[
              {
                text: "マイページに戻る",
                type: ButtonType.LINK,
                width: ButtonWidth.WIDE,
                priority: ButtonPriority.BACK,
                disabled: true,
                activeForAgent: true,
              },
            ]}
          />
        </>
        <ModalModule isOpen={openModal} onClose={onModalCloseButtonClick}>
          {ModalType.DELETE === modalType ? (
            <DeleteForm
              authenticator={selectedAuthenticator}
              onCloseButtonClick={onModalCloseButtonClick}
              onSubmitButtonClick={onDeleteModalSubmitButtonClick}
              isProcessing={isProcessing}
              ErrorMessage={modalErrorMessage}
              modalShowError={modalShowError}
            />
          ) : ModalType.EDIT ? (
            <UpdateForm
              authenticator={selectedAuthenticator}
              onCloseButtonClick={onModalCloseButtonClick}
              onSubmitButtonClick={onUpdateModalSubmitButtonClick}
              isProcessing={isProcessing}
              ErrorMessage={modalErrorMessage}
              modalShowError={modalShowError}
            />
          ) : null}
        </ModalModule>
      </TopTemplate>
    </>
  );
};

interface AuthenticationTableProps {
  isLoading: boolean;
  authenticators: Authenticator[];
  onUpdateButtonClick: Function;
  onDeleteButtonClick: Function;
}

const AuthenticatorTablePresentation = (props: AuthenticationTableProps) => {
  const authenticators = props.authenticators;

  return (
    <>
      <Table
        style={TableStyle.DEFAULT}
        header={{
          items: [
            { children: "端末名" },
            { children: "登録日時" },
            { children: " ", colSpan: 2 },
          ],
        }}
      >
        {props.isLoading ? (
          <TableRow>
            <TableCell>
              <LoadingIcon />
            </TableCell>
            <TableCell>
              <LoadingIcon />
            </TableCell>
            <TableCell colSpan={2}>
              <LoadingIcon />
            </TableCell>
          </TableRow>
        ) : (
          authenticators.map((item, index) => (
            <TableRow key={item.id != "" ? item.id : index}>
              <TableCell>{item.deviceName}</TableCell>
              <TableCell>{item.registerDate}</TableCell>
              <TableCell colSpan={2}>
                {item.id && (
                  <ButtonWrapper
                    className={style["button-wrapper"]}
                    textSize={TextSize.SMALL}
                    col={1}
                    colPc={2}
                    buttons={[
                      {
                        text: "端末名の変更",
                        priority: ButtonPriority.MEDIUM,
                        onClick: () => props.onUpdateButtonClick(item),
                      },
                      {
                        text: "削除",
                        priority: ButtonPriority.LOW,
                        onClick: () => props.onDeleteButtonClick(item),
                      },
                    ]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </Table>
    </>
  );
};

interface UpdateDeleteFormProps {
  authenticator: Authenticator;
  onCloseButtonClick: Function;
  onSubmitButtonClick: Function;
  isProcessing: boolean;
  ErrorMessage: string;
  modalShowError: boolean;
}

const UpdateForm = (props: UpdateDeleteFormProps) => {
  const authenticator = props.authenticator;
  const [newName, setNewName] = useState("");
  const modalErrorMessage = props.ErrorMessage;
  const showError = props.modalShowError;
  return (
    <>
      <Text
        textItems={[
          {
            text: "新しい認証機名を入力してください。",
          },
        ]}
        adjust={{ mb: 8 }}
      />
      <Text
        textItems={[
          {
            type: TextType.SYMBOL,
            text: "32文字以内",
          },
        ]}
        adjust={{ mb: 8 }}
        className={style["text-hint"]}
      />

      {showError ? (<ErrorMessageForm errorMessages={modalErrorMessage} />) : null}

      <Table style={TableStyle.TYPE2}>
        <TableRow>
          <TableHeader>現在</TableHeader>
          <TableCell colSpan={3}>{authenticator.deviceName}</TableCell>
        </TableRow>
        <TableRow>
          <TableHeader>変更後</TableHeader>
          <TableCell colSpan={3}>
            <TextBox
              maxLength={32}
              required={true}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </TableCell>
        </TableRow>
      </Table>
      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "OK",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.MEDIUM,
            disabled: props.isProcessing,
            onClick: () => props.onSubmitButtonClick(newName),
          },
          {
            text: "キャンセル",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.LOW,
            onClick: () => props.onCloseButtonClick(),
          },
        ]}
      />
    </>
  );
};

const DeleteForm = (props: UpdateDeleteFormProps) => {
  const authenticator = props.authenticator;

  return (
    <>
      <Text
        textItems={[
          {
            text: `「${authenticator.deviceName}」を削除してもよろしいですか？削除した場合、該当の認証機で生体認証を行うには再度登録が必要となります。`,
          },
        ]}
      />

      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "OK",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.MEDIUM,
            disabled: props.isProcessing,
            onClick: () => props.onSubmitButtonClick(),
          },
          {
            text: "キャンセル",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.LOW,
            onClick: () => props.onCloseButtonClick(),
          },
        ]}
      />
    </>
  );
};

const ErrorMessageForm = (props: { errorMessages: string }) => {
  const { errorMessages } = props;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem text={errorMessages} textStrong={true} />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

export { CSAuthenticatorManager };
